<template>
    <div class="import-feedback-drawer-file-step">
        <pendo-card
            class="import-feedback-drawer-file-step__upload-container"
            :class="{ 'import-feedback-drawer-file-step__upload-container--dropping': dragging }">
            <pendo-card title="Choose file">
                <div class="import-feedback-drawer-file-step__upload-body">
                    <img
                        class="import-feedback-drawer-file-step__upload-image"
                        width="180"
                        height="120"
                        src="@img/voc/csv-import.svg">
                    <p>
                        {{ uploadMessage }}
                    </p>
                    <pendo-button
                        theme="app"
                        type="subtle"
                        label="Help with file formatting"
                        suffix-icon="external-link" />
                    <div class="import-feedback-drawer-file-step__file-buttons">
                        <pendo-button
                            data-cy="import-feedback-drawer-file-step-download-template-button"
                            theme="app"
                            type="secondary"
                            label="Template file"
                            prefix-icon="download"
                            @click="downloadTemplate" />
                        <pendo-button
                            data-cy="import-feedback-drawer-file-step-upload-file-button"
                            class="import-feedback-drawer-file-step__upload-file"
                            theme="app"
                            type="primary"
                            :loading="csvUploading"
                            :label="uploadButtonLabel"
                            prefix-icon="upload"
                            @click="uploadFile" />
                        <input
                            ref="fileInput"
                            type="file"
                            accept=".csv"
                            data-cy="import-feedback-drawer-file-step-upload-file-input"
                            class="import-feedback-drawer-file-step__upload-file--hidden"
                            @click="$event.target.value = ''"
                            @change="handleUpload($event.target)">
                    </div>
                </div>
            </pendo-card>
        </pendo-card>
        <div
            v-if="dragging"
            class="import-feedback-drawer-file-step__drag-upload"
            :class="{ 'import-feedback-drawer-file-step__drag-upload--dropping': dragging }"
            @dragover.prevent
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <img
                src="@img/voc/pendo-sparkles.svg"
                height="100"
                width="100">
            <h5 class="help-title">
                Drop to upload!
            </h5>
            <p class="import-feedback-drawer-file-step__help-text">
                Max file size: <span class="import-feedback-drawer-file-step__help-value">100MB</span> <br>
                File type: <span class="import-feedback-drawer-file-step__help-value">CSV</span><br>
            </p>
        </div>
    </div>
</template>

<script>
import { PendoButton, PendoCard, PendoNotification } from '@pendo/components';
import { createCSVBucket, processCSV } from '@/voc/api/import.api';
import { post as gcsPost } from '@/data/gcs-service.js';
import { environmentBucket } from '@/settings/environment.utils';

export default {
    components: {
        PendoButton,
        PendoCard,
    },
    props: {
        csvFilename: {
            type: String,
            default: '',
        },
        dragging: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['file-uploaded', 'file-parsed', 'drag-leave'],
    data () {
        return {
            csvUploading: false,
        };
    },
    computed: {
        csvUploaded () {
            return this.csvFilename.length > 0;
        },
        uploadMessage () {
            return this.csvUploaded
                ? 'To update, drop your CSV file here or browse to select it.'
                : 'Drop your CSV file here or browse to select it.';
        },
        uploadButtonLabel () {
            return this.csvUploaded ? 'Change file' : 'Choose file';
        },
    },
    methods: {
        downloadTemplate () {
            const filename = 'Listen | Example CSV Upload';
            const element = document.createElement('a');
            element.setAttribute('href', `https://${environmentBucket(true)}/csv/listen-feedback-upload-template.csv`);
            element.setAttribute('download', filename);

            element.style.display = 'none';
            document.body.appendChild(element);

            element.click();

            document.body.removeChild(element);
        },
        uploadFile () {
            this.$refs.fileInput.click();
        },
        handleUpload (fileInput) {
            const file = fileInput.files[0];
            if (!file) {
                return;
            }
            this.parseCSV(file);
        },
        onDragLeave () {
            this.$emit('drag-leave');
        },
        onDrop (evt) {
            this.$emit('drag-leave');
            if (evt.dataTransfer.items) {
                const file = evt.dataTransfer.items[0].getAsFile();
                this.parseCSV(file);
            }
        },
        async parseCSV (file) {
            this.csvUploading = true;

            if (!file.type?.match(/text\/csv/i)) {
                PendoNotification({
                    type: 'error',
                    title: 'File not uploaded',
                    message: 'File type is invalid, please upload a CSV file',
                    duration: 3000,
                });
                this.csvUploading = false;

                return;
            }

            if (file.size > 100000000) {
                PendoNotification({
                    type: 'error',
                    title: 'File not uploaded',
                    message: 'File size is too large, please upload a file smaller than 100MB',
                    duration: 3000,
                });
                this.csvUploading = false;

                return;
            }

            try {
                const bucketResp = await createCSVBucket();
                const assignedFileName = bucketResp.url.split('/').pop();
                await gcsPost(bucketResp.uploadUrl, file, bucketResp.formData);
                const uploadResp = await processCSV({ name: assignedFileName });
                this.$emit('file-uploaded', {
                    data: uploadResp.rows,
                    totalRows: uploadResp.totalRows,
                    userAssignedFileName: file.name,
                    pendoAssignedFileName: assignedFileName,
                });
                PendoNotification({
                    type: 'success',
                    title: 'File uploaded',
                    message: 'Your file has been uploaded successfully',
                    duration: 3000,
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'File not uploaded',
                    message: 'There was an error uploading the CSV, please try again',
                    duration: 3000,
                });
            } finally {
                this.csvUploading = false;
            }
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer-file-step {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__upload-container {
        padding: 20px;
        width: 70%;
    }

    &__upload-body {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 10px;
        padding: 6px 0 24px 0;
        background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23128297' stroke-width='2' stroke-dasharray='8%2c 10' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
        border-radius: 4px;

        p {
            margin: 6px;
        }
    }

    &__file-buttons {
        display: flex;
        margin-top: 6px;
    }

    &__upload-file--hidden {
        display: none;
    }

    &__drag-upload {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: auto;
        justify-content: center;
        z-index: 100;
        background-color: var(--palette-gray-20);

        &--dropping {
            pointer-events: all;
        }

        * {
            pointer-events: none;
        }
    }

    &__help-value {
        color: var(--palette-gray-70);
    }

    &__help-text {
        text-align: center;
        font-size: 14px;
    }

    &__upload-image {
        margin: 16px 0;
    }
}
</style>
